import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/future/image'
import { Carousel, Picture } from '@smuchile/pkg-unimarc-components'
import {
  addToHomeData,
  clickedPromos,
  trigger,
  triggerLoginEvent,
  useSession,
  visiblePromos
} from '@smuchile/pkg-unimarc-hooks'
import { MEMBERSHIP_ID } from '@smuchile/pkg-unimarc-hooks/shared/constants'
import type MultiCarousel from 'react-multi-carousel'
import styles from './ShowCaseWeb.module.css'
import { IShowCaseWebProps } from './types'

const MOBILE_PREVIEW_GUTTER = 32
const MOBILE_PREVIEW_OFFSET = MOBILE_PREVIEW_GUTTER / 2
const carouselResponsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1
  },
  mobile: {
    breakpoint: { max: 1023, min: 0 },
    items: 1,
    partialVisibilityGutter: MOBILE_PREVIEW_GUTTER
  }
}

export const ShowCaseWebFullWidth = ({
  data,
  cfIndex,
  isMobile,
  marginBottom,
  marginTop,
  mobileMarginBottom,
  mobileMarginTop,
  salesChannel
}: IShowCaseWebProps) => {
  const { isLoading, isLoggedIn } = useSession()
  const [autoPlay, setAutoPlay] = useState(true)
  const contentObject = isMobile ? 'imageMobile' : 'imageDesktop'
  const touchSurfaceRef = useRef<HTMLDivElement>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const touchAxisRef = useRef<'horizontal' | 'vertical' | null>(null)
  const carouselRef = useRef<MultiCarousel>(null)
  const [, setNavigationReady] = useState(false)

  // react-multi-carousel locks navigation for transitionDuration after every
  // slide change: its throttled next/previous/goToSlide flip an internal flag
  // that handleDown checks on touchstart, so the first gesture right after a
  // change is silently dropped. Replace them with the raw prototype methods
  // and refresh the callbacks that captured the throttled ones at mount.
  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return

    const prototype = Object.getPrototypeOf(carousel) as {
      goToSlide: MultiCarousel['goToSlide']
      next: MultiCarousel['next']
      previous: MultiCarousel['previous']
      resetAutoplayInterval: (this: MultiCarousel) => void
    }
    carousel.goToSlide = prototype.goToSlide.bind(carousel)
    carousel.next = prototype.next.bind(carousel)
    carousel.previous = prototype.previous.bind(carousel)
    prototype.resetAutoplayInterval.call(carousel)
    setNavigationReady(true)
  }, [])

  useEffect(() => {
    const touchSurface = touchSurfaceRef.current
    if (!isMobile || !touchSurface) return undefined

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0]
      touchStartRef.current = { x: touch.clientX, y: touch.clientY }
      touchAxisRef.current = null
    }

    const handleTouchMove = (event: TouchEvent) => {
      const start = touchStartRef.current
      const touch = event.touches[0]
      if (!start || !touch) return

      const deltaX = touch.clientX - start.x
      const deltaY = touch.clientY - start.y

      if (!touchAxisRef.current) {
        const hasPassedIntentThreshold =
          Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8

        if (!hasPassedIntentThreshold) return

        touchAxisRef.current =
          Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'
      }

      if (touchAxisRef.current === 'horizontal') {
        // react-multi-carousel moves the track but does not cancel the
        // browser's native vertical scrolling for the same touch gesture.
        event.preventDefault()
      }
    }

    const handleTouchEnd = () => {
      touchStartRef.current = null
      touchAxisRef.current = null
    }

    touchSurface.addEventListener('touchstart', handleTouchStart, {
      passive: true
    })
    touchSurface.addEventListener('touchmove', handleTouchMove, {
      passive: false
    })
    touchSurface.addEventListener('touchend', handleTouchEnd, {
      passive: true
    })
    touchSurface.addEventListener('touchcancel', handleTouchEnd, {
      passive: true
    })

    return () => {
      touchSurface.removeEventListener('touchstart', handleTouchStart)
      touchSurface.removeEventListener('touchmove', handleTouchMove)
      touchSurface.removeEventListener('touchend', handleTouchEnd)
      touchSurface.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [isMobile])

  const visibleData = data?.filter((item) => {
    const itemSaleChannels = item['fields']['saleChannels']

    return (
      itemSaleChannels?.includes(salesChannel) || itemSaleChannels === undefined
    )
  })
  // Contentful sends margins as number or string; bare numbers become px.
  // Mobile prefers mobileMarginTop/mobileMarginBottom and falls back to the
  // shared marginTop/marginBottom; desktop only reads the shared ones.
  const normalizeMargin = (value: string | number | undefined) => {
    if (typeof value !== 'string' && typeof value !== 'number') return undefined
    const normalized = String(value).trim()
    if (!normalized) return undefined
    return /^-?\d*\.?\d+$/.test(normalized) ? `${normalized}px` : normalized
  }
  const topMargin = normalizeMargin(
    isMobile ? mobileMarginTop ?? marginTop : marginTop
  )
  const bottomMargin = normalizeMargin(
    isMobile ? mobileMarginBottom ?? marginBottom : marginBottom
  )
  const hasMobilePreview = isMobile && visibleData.length > 1
  // Desktop is fluid: no fixed min/max height, so the 1440px banner and its
  // container scale proportionally with the viewport instead of letterboxing.
  // Missing desktop margins default to 0 (the previous negative offsets only
  // compensated the fixed 400/450px box); mobile keeps its legacy defaults.
  const containerProps = {
    customClassName: isMobile
      ? `${styles.mobileCarousel} ${
          hasMobilePreview ? styles.mobileCarouselWithPreview : ''
        }`
      : styles.desktopCarousel,
    margin: isMobile
      ? `${topMargin ?? '0'} auto ${bottomMargin ?? '-32px'}`
      : `${topMargin ?? '0'} 0 ${bottomMargin ?? '0'} 0`,
    maxHeight: 'none',
    maxWidth: '100%',
    minHeight: 'auto',
    padding: '0',
    width: isMobile ? 'calc(100vw - 24px)' : 'min(100vw, 100%)'
  }

  const handleRedirect = async (promosData) => {
    const { reference, promotionId, promotionName } = promosData

    if (reference && promotionId && promotionName) {
      sessionStorage.setItem(
        reference,
        JSON.stringify({ promotionId, promotionName })
      )
    }

    if (promosData?.promotionId === MEMBERSHIP_ID.BANNER_HOME) {
      if (!isLoading && isLoggedIn) {
        trigger({ eventType: 'openMembershipModal' })
      } else {
        triggerLoginEvent({ eventType: 'loginModal', data: { show: true } })
      }
    }

    clickedPromos(promosData)
  }

  const handleStart = () => {
    return setAutoPlay(false)
  }

  const handleEnd = () => {
    return setAutoPlay(true)
  }

  return (
    <div
      className={`${styles.touchSurface} ${
        isMobile ? styles.mobileTouchSurface : ''
      }`}
      ref={touchSurfaceRef}
    >
      <Carousel
        additionalTransfrom={hasMobilePreview ? MOBILE_PREVIEW_OFFSET : 0}
        afterChange={() => {
          return visiblePromos({})
        }}
        arrows={!isMobile}
        autoPlay={autoPlay}
        carouselRef={carouselRef}
        containerProps={containerProps}
        customTransition='transform 450ms cubic-bezier(0.22, 1, 0.36, 1)'
        deviceType={isMobile ? 'mobile' : 'desktop'}
        dotListClass={styles.dotList}
        focusOnSelect
        infinite
        minimumTouchDrag={24}
        opacityArrows
        partialVisible={hasMobilePreview}
        pauseOnHover
        responsive={carouselResponsive}
        showDots={!isMobile}
        ssr
        transitionDuration={450}
      >
        {visibleData?.length > 0 &&
          visibleData.map((item, index) => {
            const itemSaleChannels = item['fields']['saleChannels']
            const promoData = {
              ...item,
              locationId: `${cfIndex} - ${index + 1}`
            }
            if (
              itemSaleChannels?.includes(salesChannel) ||
              itemSaleChannels === undefined
            ) {
              return (
                <Picture
                  alt={item['alt']}
                  anchor
                  catchImgRef={(ref) => {
                    addToHomeData({ ref, promoData })
                  }}
                  customClassName={styles.carouselPicture}
                  height='100%'
                  key={`${item['label']}-${index}`}
                  linkWrapper={Link}
                  nextImage={Image}
                  onClick={() => {
                    handleRedirect(promoData)
                  }}
                  onTouchEnd={handleEnd}
                  onTouchStart={handleStart}
                  priority={index === 0}
                  src={item[contentObject]?.fields?.file?.url}
                  style={{ cursor: 'pointer', height: '100%', width: '100%' }}
                  title={item[contentObject]?.fields?.title}
                  url={item['reference']}
                  width='100%'
                />
              )
            }
            return null
          })}
      </Carousel>
    </div>
  )
}
